import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './MobileAssignmentEditPage.css';
import { AssignmentApi } from '../../services/ApiService';

export default function MobileAssignmentEditPage() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();

  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    assignmentType: 'Homework',
    priority: 'Medium',
    dueDate: '',
    points: '',
    completed: false,
    status: 'Not Started'
  });

  const handleBackClick = () => {
    navigate(`/m/assignment/${assignmentId}`);
  };

  // Load assignment data
  useEffect(() => {
    const loadAssignment = async () => {
      if (!assignmentId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const response = await AssignmentApi.getAssignment(assignmentId);
        if (response.ok) {
          const data = await response.json();
          setAssignment(data);
          
          // Format due date for datetime-local input
          let formattedDueDate = '';
          if (data.dueDate) {
            const date = new Date(data.dueDate);
            if (!isNaN(date.getTime())) {
              formattedDueDate = date.toISOString().slice(0, 16);
            }
          }
          
          // Normalize priority: backend may return numeric or string
          const NUM_TO_CAP = { 0: 'None', 1: 'Low', 2: 'Medium', 3: 'High', 4: 'Urgent' };
          let mobilePriority = 'Medium';
          if (typeof data.priority === 'number' && Number.isFinite(data.priority)) {
            mobilePriority = NUM_TO_CAP[data.priority] || 'Medium';
          } else if (typeof data.priority === 'string') {
            // Capitalize first letter to match mobile select values
            const s = data.priority.trim();
            mobilePriority = s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
          }

          setFormData({
            name: data.name || '',
            description: data.description || '',
            assignmentType: data.assignmentType || 'Homework',
            priority: mobilePriority,
            dueDate: formattedDueDate,
            points: data.points || '',
            completed: data.completed || false,
            status: data.status || 'Not Started'
          });
        } else {
          throw new Error('Failed to load assignment');
        }
      } catch (error) {
        console.error('Error loading assignment:', error);
        setError(error.message || 'Failed to load assignment');
      } finally {
        setLoading(false);
      }
    };

    loadAssignment();
  }, [assignmentId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Format due date for API (server uses dueDatetime naive strings)
      let apiDueDatetime = null;
      if (formData.dueDate) {
        // convert datetime-local to naive server format YYYY-MM-DDTHH:mm:00
        const m = formData.dueDate.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2})?$/);
        apiDueDatetime = m ? `${m[1]}:00` : new Date(formData.dueDate).toISOString();
      }

      const updateData = {
        assignmentId: Number(assignmentId),
        title: formData.name,
        description: formData.description,
        assignmentType: formData.assignmentType ? String(formData.assignmentType).trim().toLowerCase() : undefined,
        priority: formData.priority,
        dueDatetime: apiDueDatetime,
        points: formData.points ? parseInt(formData.points) : null,
        completed: formData.completed,
        status: formData.status,
        eventId: assignment && (assignment.eventId || assignment.eventID || assignment.event_id),
      };

      // Remove null/undefined fields to avoid validation errors
      const sanitized = Object.keys(updateData).reduce((acc, key) => {
        const val = updateData[key];
        if (val !== null && typeof val !== 'undefined') acc[key] = val;
        return acc;
      }, {});

      console.log('MobileAssignmentEditPage: outbound sanitized', sanitized);

      const response = await AssignmentApi.updateAssignment(assignmentId, sanitized);
      
      if (response.ok) {
        // Navigate back to assignment details
        navigate(`/m/assignment/${assignmentId}`);
      } else {
        throw new Error('Failed to update assignment');
      }
    } catch (error) {
      console.error('Error saving assignment:', error);
      setError(error.message || 'Failed to save assignment');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/m/assignment/${assignmentId}`);
  };

  if (loading) {
    return (
      <div className="m-assignment-edit-screen">
        <div className="m-assignment-edit-header">
          <button className="m-back-btn" onClick={handleBackClick}>
            ← Back
          </button>
          <h1>Loading...</h1>
        </div>
        <div className="m-assignment-edit-loading">
          <div className="m-loading-spinner"></div>
          <p>Loading assignment...</p>
        </div>
      </div>
    );
  }

  if (error && !assignment) {
    return (
      <div className="m-assignment-edit-screen">
        <div className="m-assignment-edit-header">
          <button className="m-back-btn" onClick={handleBackClick}>
            ← Back
          </button>
          <h1>Error</h1>
        </div>
        <div className="m-assignment-edit-error">
          <p>Failed to load assignment: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="m-assignment-edit-screen">
      <div className="m-assignment-edit-header">
        <button className="m-back-btn" onClick={handleBackClick}>
          ← Back
        </button>
        <h1>Edit Assignment</h1>
        <div className="m-header-spacer"></div>
      </div>

      <div className="m-assignment-edit-content">
        {error && (
          <div className="m-error-message">
            <p>{error}</p>
          </div>
        )}

        <div className="m-assignment-edit-form">
          <div className="m-form-group">
            <label htmlFor="name" className="m-form-label">Assignment Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="m-form-input"
              placeholder="Enter assignment name"
              required
            />
          </div>

          <div className="m-form-row">
            <div className="m-form-group">
              <label htmlFor="assignmentType" className="m-form-label">Type</label>
              <select
                id="assignmentType"
                name="assignmentType"
                value={formData.assignmentType}
                onChange={handleInputChange}
                className="m-form-select"
              >
                <option value="Homework">Homework</option>
                <option value="Quiz">Quiz</option>
                <option value="Exam">Exam</option>
                <option value="Project">Project</option>
                <option value="Essay">Essay</option>
                <option value="Lab">Lab</option>
                <option value="Reading">Reading</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="m-form-group">
              <label htmlFor="priority" className="m-form-label">Priority</label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="m-form-select"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div className="m-form-row">
            <div className="m-form-group">
              <label htmlFor="dueDate" className="m-form-label">Due Date & Time</label>
              <input
                type="datetime-local"
                id="dueDate"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleInputChange}
                className="m-form-input"
              />
            </div>

            <div className="m-form-group">
              <label htmlFor="points" className="m-form-label">Points</label>
              <input
                type="number"
                id="points"
                name="points"
                value={formData.points}
                onChange={handleInputChange}
                className="m-form-input"
                placeholder="e.g., 100"
                min="0"
              />
            </div>
          </div>

          <div className="m-form-group">
            <label htmlFor="status" className="m-form-label">Status</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className="m-form-select"
            >
              <option value="Not Started">Not Started</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Submitted">Submitted</option>
              <option value="Graded">Graded</option>
            </select>
          </div>

          <div className="m-form-group">
            <label htmlFor="description" className="m-form-label">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="m-form-textarea"
              placeholder="Enter assignment description or instructions..."
              rows={5}
            />
          </div>

          <div className="m-form-group">
            <label className="m-checkbox-label">
              <input
                type="checkbox"
                name="completed"
                checked={formData.completed}
                onChange={handleInputChange}
                className="m-form-checkbox"
              />
              <span className="m-checkbox-text">Mark as completed</span>
            </label>
          </div>
        </div>

        <div className="m-assignment-edit-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="m-cancel-btn"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="m-save-btn"
            disabled={saving || !formData.name.trim()}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
